import { Grid } from '@mui/material';
import React, { useEffect } from 'react';
import Title from '../../components/Title';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import AccountLookup from '@Components/AccountLookup';
import Image from '@Components/Image';
import { userLogin } from '../../services/hasAdminAccess';
import SelectedUserAccountDetails from '@Components/AccountLookup/SelectedUserAccountDetails';
import useAccountLookup from '@Hook/useAccountLookup';
import CreateShipmentProvider from '../../context/CreateShipment/Provider';
import CreateShipmentContext from '../../context/CreateShipment';
import { createShipmentContextType } from '../../types/shipment';
import { getShipmentSettings } from '@Reducers/OrganizationSetup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import Loader from '@Components/Loader';
import { SuccessErrorModalOpen, SuccessErrorModalReset } from '@Reducers/SuccessErrorModal';
import { resetSelectedUser } from '@Reducers/AccountLookup';

function CreateBulkShipment() {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();

  const { userId, setUserId } = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const modalId = 'createBulkShipment';
  const { selectedUser, onOpen, setSelectedUser, loading } = useAccountLookup(modalId, true);
  const singleShipmentUser = useSelector((state: RootStateType) => state.accountLookup['CreateShipment']?.selectedUser);
  const shipmentSettingLoading = useSelector((state: RootStateType) => state.orgSetup.loading);

  const isAdmin = !userLogin();
  const isSuspended = isAdmin && selectedUser?.onBoardedStatus === 'suspended';

  useEffect(() => {
    if (isAdmin && selectedUser?.onBoardedStatus === 'suspended') {
      dispatch(resetSelectedUser({ modalId }));
      dispatch(resetSelectedUser({ modalId: 'CreateShipment' }));
      setUserId('');
    }
  }, []);

  useEffect(() => {
    return () => {
      if (selectedUser?.onBoardedStatus === 'suspended') {
        dispatch(resetSelectedUser({ modalId }));
        dispatch(resetSelectedUser({ modalId: 'CreateShipment' }));
      }
    };
  }, [selectedUser?.onBoardedStatus]);

  useEffect(() => {
    const userToUse = location?.state?.selectedUser || singleShipmentUser;
    if (userToUse) {
      setSelectedUser(userToUse);
      setUserId(userToUse._id);
    } else if (location?.state?.userId) {
      const uId = typeof location.state.userId === 'string' ? location.state.userId : location.state.userId._id;
      setUserId(uId);
    }
  }, [location?.state, singleShipmentUser]);

  useEffect(() => {
    const hasUser =
      selectedUser?._id || userId || location?.state?.selectedUser || singleShipmentUser || location?.state?.userId;
    if (isAdmin && !hasUser) {
      onOpen();
    }
  }, [isAdmin, userId, selectedUser, location?.state, singleShipmentUser]);

  useEffect(() => {
    dispatch(getShipmentSettings());
  }, []);

  useEffect(() => {
    if (isAdmin && selectedUser?.onBoardedStatus === 'suspended') {
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Oops!',
          subTitle: 'This account is suspended.',
          navigateTo: '/',
          onConfirm: () => {
            dispatch(resetSelectedUser({ modalId }));
            dispatch(resetSelectedUser({ modalId: 'CreateShipment' }));
            setUserId('');
            dispatch(SuccessErrorModalReset());
            navigate('/');
          },
        }),
      );
    }
  }, [isAdmin, selectedUser?.onBoardedStatus]);

  return (
    <Loader loading={loading || shipmentSettingLoading} overly>
      <Grid container sx={{ background: '#fff' }}>
        <Grid container item mb={4}>
          {isAdmin && (
            <>
              <AccountLookup modalId={modalId} disableUserReset getSelectedUser={user => setUserId(user._id)}>
                <Title
                  titleIconLeft={<Image src="/images/icons/BulkShipment.svg" alt="bulk_shipment" />}
                  title="Place Bulk shipment"
                  subTitle="Select the user to place the bulk shipment"
                  removeBorder
                />
              </AccountLookup>
              {selectedUser && !isSuspended && <SelectedUserAccountDetails modalId={modalId} />}
            </>
          )}
          <Title title="Bulk Shipment" subTitle="Uploaded sheet to book shipment in bulk" />
        </Grid>
        {!isSuspended && <Outlet />}
      </Grid>
    </Loader>
  );
}

export default () => (
  <CreateShipmentProvider>
    <CreateBulkShipment />
  </CreateShipmentProvider>
);
